import React from 'react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import { UserEntity } from '@data';
// Straight from the module: the barrel drags a cycle in behind it and takes unrelated suites down.
import { useClientOwnProducts } from '@data/hooks/clients';
import { PhillLogo } from '@app/components/core/login/PhillLogo';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { clientSprintRoute, clientsReportingPath } from '@app/constants/url';
import { useOrganizationId } from '@app/utils/hooks';
import ProfilePopover from './profilePopover';

export const ClientSidebar = ({ user }: { user: UserEntity }): JSX.Element => {
  const organizationId = useOrganizationId() as string;
  const transitionNavigate = useViewTransitionNav();
  const { pathname } = useLocation();
  const { data } = useClientOwnProducts(organizationId);

  // Exactly one: the list would redirect straight back here, and in flight the count is not yet known.
  const isOnlyProduct = data?.items?.length === 1;
  // Except below a sprint report — `end: false`, so anything nested there later does not go inert.
  const isSprintReport = !!useMatch({
    path: `${clientsReportingPath(organizationId)}/${clientSprintRoute}`,
    end: false,
  });
  const isInert = isOnlyProduct && !isSprintReport;

  // The logo returns to the products list with the same fade + left→right swipe as the back links —
  // but if we're already on that list, do nothing (no navigation, no animation).
  const backToProducts = (e: React.MouseEvent): void => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    if (isInert) return;
    const target = clientsReportingPath(organizationId);
    if (pathname === target || pathname === `${target}/`) return;
    transitionNavigate(target, 'back');
  };

  return (
    <header
      className="fixed left-0 right-0 top-0 z-30 bg-zinc-900 print:hidden"
      // Own view-transition-name so the fixed top bar is excluded from the sliding page root and stays put.
      style={{ viewTransitionName: 'app-navbar' }}
    >
      <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between px-4">
        {/* The play-mark, never the organization's own logo: a wordmark PNG is a different shape from
            the rest of this bar, and this header is Phill's rather than the client's. */}
        <Link
          to={clientsReportingPath(organizationId)}
          onClick={backToProducts}
          // Nothing to go back to means nothing to promise: no pointer, no hover fade.
          className={
            isInert
              ? 'flex cursor-default items-center gap-1.5'
              : 'flex items-center gap-1.5 transition-opacity hover:opacity-80'
          }
        >
          <PhillLogo className="h-11 w-11 text-zinc-50" />
        </Link>
        <div className="flex items-center gap-2">
          {/* The same menu our own people get, minus what is ours: signing out, and bringing a
              coworker onto these reports. */}
          <div className="w-60 text-zinc-50">
            <ProfilePopover user={user} side="bottom" align="end" isEmployee={false} />
          </div>
        </div>
      </div>
    </header>
  );
};
